import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import moment from 'moment-timezone';
import React, { useEffect, useRef, useState } from 'react';
import { DefaultDateTimePickerInput } from '@/datetime/DefaultDateTimePickerInput.atom';
import { isRelativeDateShorthand } from '@/datetime/dateTime.utilities';
import {
  expandLocalizedTokens,
  formatHasTimeElements,
  formatUses24HourTime,
} from '@/datetime/dateTimeFormatting.utilities';
import { useFlatpickr } from '@/datetime/flatpickr/useFlatpickr.hook';
import { doTrack } from '@/track/track.service';
import { focusAndSelectElement } from '@/utilities/dom.utilities';
import type { Moment } from 'moment-timezone';
import type { DateTimePickerInputProps } from '@/datetime/DefaultDateTimePickerInput.atom';
import type { Timezone } from '@/datetime/timezone.service';
import type { TrackCategory } from '@/track/track.constants';

/**
 * DateTimePicker component using Flatpickr for date/time selection
 *
 * This component exists to obfuscate flatpickr implementation details and hacks required
 *   to make it work for our purposes. It is only meant to be used inside `DateTimeEntry`,
 *   not as a standalone component. It is tested as part of `DateTimeEntry`.
 */
export interface DateTimePickerProps {
  value: Moment | undefined;
  testId: string;
  localizedDateFormat: string;
  timezone: Timezone;
  onDatetimeSelect: (date: Moment | undefined) => void;
  onManualDatetimeInput: (dateStr: string) => Moment | undefined;
  trackCategory: TrackCategory;
  trackAction: string;
  trackInformation?: Record<string, any>;
  extraClassNames?: string;
  xstyle?: stylex.StyleXStyles;
  calendarExtraClassName?: string;
  inputHeight: number;
  enableSeconds: boolean;
  isInsideModal: boolean;
  showError?: boolean;
  /**
   * The minimum date that a user can start picking from (inclusive).
   */
  minDate?: moment.Moment;
  /**
   * The maximum date that a user can pick to (inclusive).
   */
  maxDate?: moment.Moment;
  /**
   * Adjusts the step for the minute input
   */
  minuteIncrement?: number;
  /**
   * Flag to control whether the picker is open. If undefined, the picker will be open by default.
   */
  isOpen?: boolean;
  /**
   * Flag to control whether the date should be dynamically updated when only month/year changes in the picker.
   * When false, clicking "Done" will not auto-update the selected date to match the current month/year.
   * Defaults to true.
   */
  autoUpdateDate?: boolean;
  /** External lifecycle signal that asks Flatpickr to retry initialization. */
  initializationSignal?: unknown;
  /**
   * Optional component to render instead of the default TextField input.
   * Must forward its ref to the underlying <input> DOM element so flatpickr can attach.
   * Receives DateTimePickerInputProps. Width/height should be controlled via extraClassNames.
   */
  inputComponent?: React.ComponentType<DateTimePickerInputProps>;
}

export const DateTimePicker = ({
  value,
  testId,
  localizedDateFormat,
  timezone,
  onDatetimeSelect,
  onManualDatetimeInput,
  trackCategory = 'DateTimePicker',
  trackAction = 'Date changed',
  trackInformation = {},
  extraClassNames = '',
  xstyle,
  calendarExtraClassName,
  inputHeight,
  enableSeconds,
  isInsideModal,
  showError = false,
  minDate,
  maxDate,
  minuteIncrement,
  isOpen,
  autoUpdateDate,
  initializationSignal,
  inputComponent,
}: DateTimePickerProps) => {
  const INPUT_PADDING = 10;
  const MIN_WIDTH = 50;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const hiddenSpanRef = useRef<HTMLSpanElement | null>(null);
  const [inputWidth, setInputWidth] = useState<number>(MIN_WIDTH);

  const expandedDateFormat = expandLocalizedTokens(localizedDateFormat);
  const enableTime = formatHasTimeElements(expandedDateFormat);
  const enableTime24hr = formatUses24HourTime(expandedDateFormat);

  const setInputWidthToFitValue = () => {
    if (!inputRef.current || !hiddenSpanRef.current) return;

    const hiddenSpan = hiddenSpanRef.current;
    hiddenSpan.textContent = value && value.tz ? value.tz(timezone.name).format(localizedDateFormat) : '';

    const newWidth = hiddenSpan.offsetWidth;
    setInputWidth(Math.max(newWidth + INPUT_PADDING, MIN_WIDTH));
  };

  useEffect(setInputWidthToFitValue, [value?.valueOf(), localizedDateFormat]);

  const handleManualInput = (dateString: string) => {
    doTrack(trackCategory, trackAction, {
      ...trackInformation,
      input: dateString,
      dateChangeTrigger: 'manual',
      inputType: isRelativeDateShorthand(dateString) ? 'relative' : 'absolute',
    });

    const parsedDatetime = onManualDatetimeInput(dateString);
    return parsedDatetime || value;
  };

  const handleDatetimeSelect = (date: Moment | undefined) => {
    doTrack(trackCategory, trackAction, { ...trackInformation, dateChangeTrigger: 'datePickerSelect' });
    onDatetimeSelect(date ? moment(date) : undefined);
  };

  const handleOpen = () => {
    doTrack(trackCategory, trackAction, { ...trackInformation, dateChangeTrigger: 'open' });
  };

  const handleClose = () => {
    doTrack(trackCategory, trackAction, { ...trackInformation, dateChangeTrigger: 'close' });
  };

  const handleDoneClick = () => {
    doTrack(trackCategory, trackAction, { ...trackInformation, dateChangeTrigger: 'done' });
  };

  const handleCancelClick = () => {
    doTrack(trackCategory, trackAction, { ...trackInformation, dateChangeTrigger: 'cancel' });
  };

  useFlatpickr({
    anchorRef: inputRef,
    value,
    timezone,
    localizedDateFormat,
    enableSeconds,
    enableTime,
    enableTime24hr,
    onDateSelect: handleDatetimeSelect,
    onManualInput: handleManualInput,
    isInsideModal,
    minDate,
    maxDate,
    minuteIncrement,
    isOpen,
    autoUpdateDate,
    calendarExtraClassName,
    initializationSignal,
    onOpen: handleOpen,
    onClose: handleClose,
    onDoneClick: handleDoneClick,
    onCancelClick: handleCancelClick,
  });

  const InputComponent = inputComponent ?? null;

  return (
    <>
      <span data-testid={`${testId}_dateTimePicker`}>
        <span
          {...stylex.props(xstyle)}
          ref={hiddenSpanRef}
          aria-hidden="true"
          className={clsx(extraClassNames, 'hiddenSpan', stylex.props(xstyle).className)}
        />
        {InputComponent ? (
          <InputComponent
            ref={inputRef}
            testId={testId}
            placeholder={expandedDateFormat}
            showError={showError}
            extraClassNames={extraClassNames}
            xstyle={xstyle}
            onFocus={focusAndSelectElement}
          />
        ) : (
          <DefaultDateTimePickerInput
            ref={inputRef}
            testId={testId}
            placeholder={expandedDateFormat}
            showError={showError}
            extraClassNames={extraClassNames}
            xstyle={xstyle}
            inputWidth={inputWidth}
            inputHeight={inputHeight}
            onFocus={focusAndSelectElement}
          />
        )}
      </span>
    </>
  );
};
